import {
  addMonths,
  formatDateShortYear,
  formatInteger,
  formatMonthName,
  formatMonthShort,
  formatNumber,
  formatPercent,
  formatSigned,
  TV_INTERNAL_ROWS,
  TV_PROJECT_PAGE_SECONDS,
  type TvInternalProject,
  type TvInternalResponse,
  type TvInternalRoadmapItem,
} from '@codepass/shared';
import { cn } from '@/lib/cn';
import { TvScreenPage } from '@/pages/TvDashboard';
import { Box, STATUS, statusCounts, TopBar, usePage } from '@/pages/TvProjects';

/**
 * Storskaerm 3 - "Hvad bygger vi til os selv?" The design is the PDF in docs/dashboard
 * (Storskaerm-3-Intern-udvikling); there is no written spec. Drawn in storskaerm 2's tokens.
 *
 * The data is the internal boards in OnePro and the time not spent on customers - see
 * tv-internal.ts in the API. The roadmap is Orchestrator's internal projects. Two things in the PDF
 * have no source yet and are left out: the system per board and a roadmap project's start.
 * "Timer pr. system" is per project instead.
 */

const RESPONSE_FIELDS = Object.keys({
  generated_at: true,
  updated_at: true,
  stale: true,
  display: true,
  projects: true,
  roadmap: true,
  month: true,
  months: true,
  budget_hours: true,
  by_project: true,
  completed: true,
} satisfies Record<keyof TvInternalResponse, true>);

export function TvInternalPage() {
  return (
    <TvScreenPage<TvInternalResponse>
      screen="internal"
      apiPath="/dashboard/tv/internal"
      fields={RESPONSE_FIELDS}
      className="bg-tv2-bg"
      render={(data, stale) => <TvInternalView data={data} stale={stale} />}
    />
  );
}

const COLUMNS = 'minmax(0,2.4fr) minmax(0,1.6fr) minmax(0,1.5fr) minmax(0,1.2fr) minmax(0,1.5fr)';
const ROADMAP_COLUMNS =
  'minmax(0,0.3fr) minmax(0,2.6fr) minmax(0,1.3fr) minmax(0,0.8fr) minmax(0,1.2fr)';

export function TvInternalView({ data, stale }: { data: TvInternalResponse; stale: boolean }) {
  const { page, pages } = usePage(data.projects.length, TV_INTERNAL_ROWS, TV_PROJECT_PAGE_SECONDS);
  const shown = data.projects.slice(page * TV_INTERNAL_ROWS, (page + 1) * TV_INTERNAL_ROWS);
  const count = data.projects.length;
  const roadmap = usePage(data.roadmap.length, TV_INTERNAL_ROWS, TV_PROJECT_PAGE_SECONDS);
  const roadmapFirst = roadmap.page * TV_INTERNAL_ROWS;
  const planned = data.roadmap.slice(roadmapFirst, roadmapFirst + TV_INTERNAL_ROWS);

  return (
    <div className="flex h-full flex-col gap-3 px-2">
      <TopBar
        eyebrow="INTERN UDVIKLING · STATUS"
        title="Hvad bygger vi til os selv?"
        counts={statusCounts(data.projects)}
        updatedAt={data.updated_at}
        stale={stale}
      />

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)] gap-6">
        <section className="flex min-h-0 flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <p className="text-[20px] font-bold">
              I gang{' '}
              <span className="font-normal text-tv2-muted">
                · {formatInteger(count)} {count === 1 ? 'projekt' : 'projekter'}
              </span>
            </p>
            <p className="text-[12px] text-tv2-muted">
              sorteret efter forventet afsluttet
              {pages > 1 ? ` · side ${page + 1} af ${pages}` : ''}
            </p>
          </div>
          <div
            className="grid gap-[18px] px-5 text-[12px] font-semibold tracking-[1.5px] text-tv2-muted"
            style={{ gridTemplateColumns: COLUMNS }}
          >
            <span>PROJEKT</span>
            <span>UDVIKLERE</span>
            <span>TIMER / ESTIMAT</span>
            <span>AFSLUTTET</span>
            <span>STATUS</span>
          </div>
          <div
            key={page}
            className="grid min-h-0 flex-1 animate-tvfade content-between"
            /* A place for every row of a full page, so a short list stands where a full one would. */
            style={{ gridTemplateRows: `repeat(${TV_INTERNAL_ROWS}, 58px)` }}
          >
            {shown.length === 0 ? (
              <p className="p-5 text-[15px] text-tv2-soft">Ingen interne projekter i gang</p>
            ) : (
              shown.map((project) => <InternalRow key={project.name} project={project} />)
            )}
          </div>
        </section>

        <section className="flex min-h-0 flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <p className="text-[20px] font-bold">
              Roadmap{' '}
              <span className="font-normal text-tv2-muted">
                · {formatInteger(data.roadmap.length)}{' '}
                {data.roadmap.length === 1 ? 'projekt' : 'projekter'}
              </span>
            </p>
            <p className="text-[12px] text-tv2-muted">
              godkendte først
              {roadmap.pages > 1 ? ` · side ${roadmap.page + 1} af ${roadmap.pages}` : ''}
            </p>
          </div>
          <div
            className="grid gap-[18px] px-5 text-[12px] font-semibold tracking-[1.5px] text-tv2-muted"
            style={{ gridTemplateColumns: ROADMAP_COLUMNS }}
          >
            <span>#</span>
            <span>PROJEKT</span>
            <span>SYSTEM</span>
            <span>ESTIMAT</span>
            <span>GODKENDELSE</span>
          </div>
          <div
            key={roadmap.page}
            className="grid min-h-0 flex-1 animate-tvfade content-between"
            style={{ gridTemplateRows: `repeat(${TV_INTERNAL_ROWS}, 58px)` }}
          >
            {planned.length === 0 ? (
              <p className="p-5 text-[15px] text-tv2-soft">Intet på roadmappen</p>
            ) : (
              planned.map((item, index) => (
                <RoadmapRow key={item.project_number} rank={roadmapFirst + index + 1} item={item} />
              ))
            )}
          </div>
        </section>
      </div>

      <div className="grid h-[210px] shrink-0 grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1.25fr)_minmax(0,1.25fr)] gap-4">
        <MonthBox month={data.month} />
        <MonthsBox months={data.months} budget={data.budget_hours} />
        <Box title={`TIMER PR. PROJEKT · ${data.month.month_start.slice(0, 4)}`}>
          <HoursBars rows={data.by_project} />
        </Box>
        <Box title="AFSLUTTET · SENESTE 30 DAGE">
          {data.completed.length === 0 ? (
            <p className="text-[15px] text-tv2-soft">Intet afsluttet</p>
          ) : (
            <div className="flex flex-col">
              {data.completed.map((task, index) => (
                <div
                  key={`${task.name}-${index}`}
                  className="flex items-baseline justify-between gap-3 border-b border-tv2-divider py-1.5 text-[14px]"
                >
                  <span className="truncate font-semibold">{task.name}</span>
                  <span className="tabular shrink-0 text-[12px] text-tv2-soft">
                    {formatDateShortYear(task.date)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Box>
      </div>
    </div>
  );
}

function InternalRow({ project }: { project: TvInternalProject }) {
  const status = STATUS[project.status];
  const over = project.estimate_hours !== null && project.used_hours > project.estimate_hours;
  const margin = project.margin_days;

  return (
    <div
      className="grid h-[58px] shrink-0 items-center gap-[18px] rounded-lg border-t-2 border-tv2-accent bg-tv2-surface px-5"
      style={{ gridTemplateColumns: COLUMNS }}
    >
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-[20px] font-semibold">{project.name}</span>
        {project.workspace ? (
          <span className="truncate text-[13px] text-tv2-soft">{project.workspace}</span>
        ) : null}
      </div>
      {project.developers.length > 0 ? (
        /* Everyone with time on the board can be five or more names: two smaller lines, not one cut. */
        <p className="line-clamp-2 text-[15px] font-semibold leading-tight">
          {project.developers.join(', ')}
        </p>
      ) : (
        <p className="text-[18px] font-semibold text-tv2-amber">Ikke tildelt</p>
      )}
      <p className="flex items-baseline gap-2 whitespace-nowrap">
        <b
          className={cn(
            'tabular text-[22px] font-bold',
            over ? 'text-tv2-coral' : 'text-tv2-green',
          )}
        >
          {formatInteger(project.used_hours)}
        </b>
        <span className="text-[14px] text-tv2-soft">
          {project.estimate_hours === null
            ? 't · intet estimat'
            : `af ${formatInteger(project.estimate_hours)} t`}
        </span>
        {project.used_pct === null ? null : (
          <span
            className={cn(
              'tabular text-[14px] font-semibold',
              over ? 'text-tv2-coral' : 'text-tv2-green',
            )}
          >
            {formatPercent(project.used_pct)}
          </span>
        )}
      </p>
      <div className="flex items-baseline gap-2.5 whitespace-nowrap">
        <b className="tabular text-[18px] font-semibold">
          {formatDateShortYear(project.expected_finish)}
        </b>
        {margin === null ? null : (
          <span
            className={cn(
              'tabular text-[14px] font-semibold',
              margin < 0 ? 'text-tv2-coral' : 'text-tv2-green',
            )}
          >
            {formatSigned(margin)} d
          </span>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-2.5">
        <span className={cn('h-3 w-3 shrink-0 rounded-full', status.dot)} aria-hidden />
        <div className="flex min-w-0 flex-col leading-[1.15]">
          <b className={cn('text-[17px] font-semibold', status.text)}>{status.label}</b>
          <span className="truncate text-[13px] text-tv2-soft">{project.status_reason}</span>
        </div>
      </div>
    </div>
  );
}

/** Not started yet, so a dashed edge - as in the PDF. */
function RoadmapRow({ rank, item }: { rank: number; item: TvInternalRoadmapItem }) {
  return (
    <div
      className="grid h-[58px] shrink-0 items-center gap-[18px] rounded-lg border-t-2 border-dashed border-tv2-divider-strong bg-tv2-surface px-5"
      style={{ gridTemplateColumns: ROADMAP_COLUMNS }}
    >
      <b className="tabular text-[18px] font-semibold text-tv2-soft">{rank}</b>
      <span className="truncate text-[20px] font-semibold">{item.title}</span>
      <div className="min-w-0">
        <span className="inline-block max-w-full truncate rounded-full bg-tv2-bg px-2.5 py-1 text-[13px] font-semibold">
          {item.system}
        </span>
      </div>
      <b className="tabular whitespace-nowrap text-[18px] font-semibold">
        {formatInteger(item.estimate_hours)} t
      </b>
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            'h-3 w-3 shrink-0 rounded-full',
            item.approved ? 'bg-tv2-green' : 'bg-tv2-amber',
          )}
          aria-hidden
        />
        <b
          className={cn(
            'text-[17px] font-semibold',
            item.approved ? 'text-tv2-green' : 'text-tv2-amber',
          )}
        >
          {item.approved ? 'Godkendt' : 'Afventer'}
        </b>
      </div>
    </div>
  );
}

/** "Internt i oktober": the month's internal share of all hours - amber, it earns nothing. */
function MonthBox({ month }: { month: TvInternalResponse['month'] }) {
  return (
    <Box title={`INTERNT I ${formatMonthName(month.month_start).toUpperCase()}`} warn>
      <p className="flex items-baseline gap-2.5">
        <b className="tabular text-[56px] font-bold leading-[0.95] text-tv2-amber">
          {formatPercent(month.share_pct)}
        </b>
        <span className="text-[18px] font-medium">af alle timer</span>
      </p>
      <p className="text-[14px] text-tv2-soft">
        {formatNumber(month.internal_hours, 1)} interne timer · tjener ingen penge
      </p>
      <div className="mt-auto flex gap-6 border-t border-tv2-divider-strong pt-2">
        <div>
          <b className="tabular block text-[20px] font-bold">
            {formatPercent(month.previous_share_pct)}
          </b>
          <span className="text-[12px] text-tv2-muted">
            {formatMonthName(addMonths(month.month_start, -1))}
          </span>
        </div>
        <div>
          <b className="tabular block text-[20px] font-bold">{formatInteger(month.year_hours)} t</b>
          <span className="text-[12px] text-tv2-muted">år til dato</span>
        </div>
      </div>
    </Box>
  );
}

/**
 * Six months of internal hours against the budget line - under the line is good. Over it in coral,
 * this month in green, the rest in the bar color. CSS bars, like the rest of Codepass' charts.
 */
function MonthsBox({ months, budget }: { months: TvInternalResponse['months']; budget: number }) {
  const top = Math.max(budget, ...months.map((month) => month.internal_hours)) * 1.15 || 1;
  const height = (hours: number) => `${Math.min(100, (hours / top) * 100)}%`;
  const last = months.length - 1;

  return (
    <Box
      title="INTERNE TIMER PR. MÅNED"
      aside={
        /* The budget is named here and not on the line, where a month over it would cover it. */
        <span className="whitespace-nowrap text-[12px] text-tv2-muted">
          linjen: budget <b className="font-semibold text-tv2-ink">{formatInteger(budget)} t</b> ·
          under er godt
        </span>
      }
    >
      <div className="relative flex min-h-0 flex-1 items-end gap-3">
        <div
          className="absolute inset-x-0 z-10 border-t-2 border-dashed border-tv2-ink/80"
          style={{ bottom: height(budget) }}
        />
        {months.map((month, index) => (
          <div key={month.month_start} className="flex h-full flex-1 flex-col justify-end">
            <div
              className={cn(
                'flex min-h-[22px] items-start justify-center rounded-t-[4px] pt-0.5 text-[14px] font-bold',
                index === last
                  ? 'bg-tv2-green text-tv2-on-color'
                  : month.internal_hours > budget
                    ? 'bg-tv2-coral text-tv2-on-color'
                    : 'bg-tv2-bar text-tv2-ink',
              )}
              style={{ height: height(month.internal_hours) }}
            >
              {formatNumber(month.internal_hours, month.internal_hours % 1 === 0 ? 0 : 1)}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3 text-[13px] text-tv2-soft">
        {months.map((month, index) => (
          <span
            key={month.month_start}
            className={cn('flex-1 text-center', index === last && 'font-semibold text-tv2-ink')}
          >
            {formatMonthShort(month.month_start)}
          </span>
        ))}
      </div>
    </Box>
  );
}

function HoursBars({ rows }: { rows: TvInternalResponse['by_project'] }) {
  const top = Math.max(1, ...rows.map((row) => row.hours));
  if (rows.length === 0)
    return <p className="text-[15px] text-tv2-soft">Ingen interne timer endnu</p>;
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((row) => (
        <div key={row.name} className="relative h-[22px] overflow-hidden rounded bg-tv2-bg">
          <i
            className="absolute inset-y-0 left-0 bg-tv2-bar"
            style={{ width: `${(row.hours / top) * 100}%` }}
          />
          <div className="relative flex h-[22px] items-center justify-between px-2.5 text-[13px]">
            <span className="truncate font-semibold">{row.name}</span>
            <b className="tabular font-bold">{formatInteger(row.hours)} t</b>
          </div>
        </div>
      ))}
    </div>
  );
}
